import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import type { PromotionFormData } from '@/lib/validations/promotion'
import type { AxiosError } from 'axios'
import type { ErrorResponse } from '@/types/common'
import type { PromotionItem } from '@/types/promotion'
import { PromotionStatus } from '@/types/promotion'
import { promotionDetailQueryOptions } from '@/lib/queryOptions'
import FormPromotion, {
  getCurrentDateTimeLocal,
} from '@/components/PromotionManagement/FormPromotion'
import { PromotionService } from '@/services/promotion'

export const Route = createFileRoute(
  '/_authenticated/promotions/update/$promotionId',
)({
  loader: ({ context: { queryClient }, params: { promotionId } }) => {
    return queryClient.ensureQueryData(promotionDetailQueryOptions(promotionId))
  },
  component: RouteComponent,
})

function RouteComponent() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { promotionId } = Route.useParams()

  const queryDetail = useSuspenseQuery(promotionDetailQueryOptions(promotionId))

  const isLoading = queryDetail.isLoading

  const promotion = queryDetail.data.data

  const mutationEdit = useMutation({
    mutationFn: (body: Partial<PromotionItem>) => {
      return PromotionService.update(body, promotionId)
    },
    onSuccess: () => {
      toast.success('Cập nhật khuyến mãi thành công')
      queryClient.invalidateQueries({
        queryKey: ['promotion', promotionId],
      })
      navigate({ to: '/promotions' })
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message)
    },
  })

  const promotionDetail = (payload: PromotionItem) => {
    const data: PromotionFormData = {
      ...payload,
      discountPercentage: payload.discountPercentage.toString(),
      quota: payload.quota.toString(),
      minOrderValueVND: payload.minOrderValueVND.toString(),
      maxDiscountValueVND: payload.maxDiscountValueVND.toString(),
      minOrderValueUSD: payload.minOrderValueUSD.toString(),
      maxDiscountValueUSD: payload.maxDiscountValueUSD.toString(),
      validFrom: getCurrentDateTimeLocal(payload.validFrom),
      validTo: getCurrentDateTimeLocal(payload.validTo),
    }
    return data
  }

  const handleSubmit = (
    changes: Partial<PromotionFormData>,
    formData?: PromotionFormData,
  ) => {
    const payload: Partial<PromotionItem> = {
      ...changes,
      discountPercentage: changes.discountPercentage
        ? Number(changes.discountPercentage)
        : undefined,
      quota: changes.quota ? Number(changes.quota) : undefined,
      minOrderValueVND: changes.minOrderValueVND
        ? Number(changes.minOrderValueVND)
        : undefined,
      maxDiscountValueVND: changes.maxDiscountValueVND
        ? Number(changes.maxDiscountValueVND)
        : undefined,
      minOrderValueUSD: changes.minOrderValueUSD
        ? Number(changes.minOrderValueUSD)
        : undefined,
      maxDiscountValueUSD: changes.maxDiscountValueUSD
        ? Number(changes.maxDiscountValueUSD)
        : undefined,
      validFrom: changes.validFrom
        ? new Date(changes.validFrom).toISOString()
        : undefined,
      validTo: changes.validTo
        ? new Date(changes.validTo).toISOString()
        : undefined,
      status: PromotionStatus.ACTIVE,
      applicableProductIds: formData?.applicableProductIds || [],
    }
    mutationEdit.mutate(payload)
  }
  return (
    <div>
      <FormPromotion
        initialData={promotionDetail(promotion)}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        isCreate={false}
      />
    </div>
  )
}
